/**
 * Iter51 E33 (Phase 1) — Reusable Import Flow component.
 *
 * Encapsulates the "select file → choose action → confirm" UX flow so the same
 * pattern works for all 3 import sources (Easy / Salesforce / CRM Legacy).
 *
 * Behaviour:
 *   - Stage 1: shows the file picker
 *   - Stage 2: file selected → shows action cards (Pre-flight / Dry-Run / Import)
 *     + "Schimbă fișierul" button to reset
 *   - Stage 3: clicking "Import REAL" opens a confirm dialog before triggering the upload
 *
 * Props:
 *   - source: "easy" | "salesforce" | "crm"
 *   - accept: file extension filter (e.g. ".xlsx,.xls")
 *   - colorScheme: "blue" | "purple" | "orange" (tab accent)
 *   - showPreflight: bool (only SF has preflight in Phase 1)
 *   - showDryRun:    bool (only SF has dry-run in Phase 1)
 *   - onRealImport(file): async — called when user confirms a real import
 *   - onPreflight(file):  async — called for pre-flight click (read-only)
 *   - onDryRun(file):     async — called for dry-run click (read-only)
 *   - busy:    bool (disables actions during in-flight operations)
 *   - extraConfirmHint: string|null — extra warning shown in the confirm dialog
 */
import React, { useState, useCallback } from "react";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Upload,
  FileSpreadsheet,
  RefreshCw,
  FlaskConical,
  ShieldCheck,
  AlertTriangle,
  X,
} from "lucide-react";

const SOURCE_LABELS = {
  easy: "Easy App",
  salesforce: "Salesforce",
  crm: "CRM Legacy",
};

export const ImportFlow = ({
  source,
  accept = ".xlsx,.xls",
  colorScheme = "blue",
  showPreflight = false,
  showDryRun = false,
  onRealImport,
  onPreflight,
  onDryRun,
  busy = false,
  extraConfirmHint = null,
}) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const onPickFile = useCallback((e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const lower = f.name.toLowerCase();
    if (!lower.endsWith(".xlsx") && !lower.endsWith(".xls")) {
      e.target.value = "";
      return;
    }
    setSelectedFile(f);
    e.target.value = "";
  }, []);

  const handleReset = useCallback(() => setSelectedFile(null), []);

  const handlePreflight = useCallback(() => {
    if (selectedFile && onPreflight) onPreflight(selectedFile);
  }, [selectedFile, onPreflight]);

  const handleDryRun = useCallback(() => {
    if (selectedFile && onDryRun) onDryRun(selectedFile);
  }, [selectedFile, onDryRun]);

  const handleConfirmedImport = useCallback(async () => {
    setConfirmOpen(false);
    if (selectedFile && onRealImport) {
      await onRealImport(selectedFile);
      // After kicking off the real import, clear local state so user has to
      // explicitly re-select a file to start another import.
      setSelectedFile(null);
    }
  }, [selectedFile, onRealImport]);

  // === Stage 1: no file selected → show picker ===
  if (!selectedFile) {
    return (
      <div className="space-y-2" data-testid={`import-flow-${source}-picker`}>
        <Label htmlFor={`${source}-file`}>Selectează fișier Excel ({accept})</Label>
        <Input
          id={`${source}-file`}
          type="file"
          accept={accept}
          onChange={onPickFile}
          disabled={busy}
          data-testid={`${source}-file-input`}
        />
        <p className="text-xs text-slate-500">
          După ce alegi fișierul, vei putea alege ce să faci cu el (verificare, simulare, sau import real).
        </p>
      </div>
    );
  }

  // === Stage 2: file selected → show action panel ===
  const accentClasses = {
    blue:   { card: "border-blue-300 bg-blue-50",     icon: "text-blue-600" },
    purple: { card: "border-purple-300 bg-purple-50", icon: "text-purple-600" },
    orange: { card: "border-orange-300 bg-orange-50", icon: "text-orange-600" },
  }[colorScheme] || { card: "border-slate-300 bg-slate-50", icon: "text-slate-600" };

  return (
    <>
      <div
        className={`rounded-lg border-2 ${accentClasses.card} p-4 space-y-4`}
        data-testid={`import-flow-${source}-staged`}
      >
        {/* Selected file header */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-2 min-w-0">
            <FileSpreadsheet className={`h-5 w-5 mt-0.5 shrink-0 ${accentClasses.icon}`} />
            <div className="min-w-0">
              <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold">
                Fișier selectat
              </p>
              <p
                className="font-mono text-sm text-slate-900 truncate"
                title={selectedFile.name}
                data-testid={`${source}-selected-filename`}
              >
                {selectedFile.name}
              </p>
              <p className="text-xs text-slate-500 mt-0.5">
                {(selectedFile.size / 1024).toFixed(1)} KB · neimportat încă
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleReset}
            disabled={busy}
            data-testid={`${source}-reset-file-btn`}
            className="shrink-0"
          >
            <X className="h-4 w-4 mr-1" /> Schimbă fișierul
          </Button>
        </div>

        {/* Action grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {/* Pre-flight (read-only) */}
          {showPreflight && (
            <button
              type="button"
              onClick={handlePreflight}
              disabled={busy}
              data-testid={`${source}-action-preflight`}
              className="text-left rounded-lg border-2 border-teal-300 bg-teal-50 hover:bg-teal-100 hover:border-teal-400 transition-colors p-3 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="flex items-center gap-2 mb-1">
                <ShieldCheck className="h-4 w-4 text-teal-700" />
                <span className="font-semibold text-sm text-teal-900">Verifică headere</span>
                <span className="ml-auto text-[10px] uppercase tracking-wide text-teal-700 bg-teal-100 px-1.5 py-0.5 rounded">
                  read-only
                </span>
              </div>
              <p className="text-xs text-teal-800">
                Validează coloanele + calitatea datelor (% rânduri goale).
                Nu scrie nimic în DB.
              </p>
            </button>
          )}

          {/* Dry-Run (read-only) */}
          {showDryRun && (
            <button
              type="button"
              onClick={handleDryRun}
              disabled={busy}
              data-testid={`${source}-action-dryrun`}
              className="text-left rounded-lg border-2 border-indigo-300 bg-indigo-50 hover:bg-indigo-100 hover:border-indigo-400 transition-colors p-3 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="flex items-center gap-2 mb-1">
                <FlaskConical className="h-4 w-4 text-indigo-700" />
                <span className="font-semibold text-sm text-indigo-900">Simulare (Dry-Run)</span>
                <span className="ml-auto text-[10px] uppercase tracking-wide text-indigo-700 bg-indigo-100 px-1.5 py-0.5 rounded">
                  read-only
                </span>
              </div>
              <p className="text-xs text-indigo-800">
                Calculează câte rânduri ar fi create/actualizate/sărite, fără scriere în DB.
              </p>
            </button>
          )}

          {/* REAL Import (writes to DB) */}
          <button
            type="button"
            onClick={() => setConfirmOpen(true)}
            disabled={busy}
            data-testid={`${source}-action-import`}
            className="text-left rounded-lg border-2 border-red-300 bg-red-50 hover:bg-red-100 hover:border-red-400 transition-colors p-3 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <div className="flex items-center gap-2 mb-1">
              <Upload className="h-4 w-4 text-red-700" />
              <span className="font-semibold text-sm text-red-900">Pornește import REAL</span>
              <span className="ml-auto text-[10px] uppercase tracking-wide text-red-800 bg-red-200 px-1.5 py-0.5 rounded">
                scrie în DB
              </span>
            </div>
            <p className="text-xs text-red-800">
              Procesează fișierul și salvează rezultatele în baza de date. Acțiunea nu poate fi anulată automat.
            </p>
          </button>
        </div>

        {/* Tip when only "real import" is available (Easy/CRM) */}
        {!showPreflight && !showDryRun && (
          <p className="text-xs text-slate-500">
            💡 Verificarea pre-import și simularea (Dry-Run) sunt disponibile momentan doar pentru Salesforce. Vor fi adăugate aici în Faza 2 a refactor-ului.
          </p>
        )}
      </div>

      {/* Confirm dialog for REAL import */}
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid={`${source}-import-confirm`}>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-900">
              <AlertTriangle className="h-5 w-5 text-red-600" />
              Confirmare import REAL ({SOURCE_LABELS[source] || source})
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm">
                <p>
                  Vei porni un import care{" "}
                  <strong className="text-red-700">scrie în baza de date Production</strong>.
                  Fișierul:
                </p>
                <p className="font-mono text-xs bg-slate-100 px-2 py-1 rounded break-all">
                  {selectedFile.name}
                </p>
                {extraConfirmHint && (
                  <p className="text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 text-xs">
                    {extraConfirmHint}
                  </p>
                )}
                <p className="text-xs text-slate-600">
                  După pornire, importul rulează în fundal. Îl poți opri din „Istoric Importuri" prin butonul „Oprește" dar rândurile deja procesate vor rămâne în DB.
                </p>
                {showPreflight && (
                  <p className="text-xs text-teal-700 bg-teal-50 border border-teal-200 rounded p-2">
                    💡 Recomandare: rulează întâi <strong>„Verifică headere"</strong> dacă nu ai făcut-o încă.
                  </p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`${source}-import-cancel-btn`}>
              Anulează
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmedImport}
              className="bg-red-600 hover:bg-red-700"
              data-testid={`${source}-import-confirm-btn`}
            >
              <Upload className="h-4 w-4 mr-1" /> Da, pornește importul real
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {busy && (
        <div className="mt-3 flex items-center gap-2 text-sm text-slate-600">
          <RefreshCw className="h-4 w-4 animate-spin" /> Se procesează cererea...
        </div>
      )}
    </>
  );
};

export default ImportFlow;
